import { computed, onScopeDispose, shallowRef, toValue, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import { createProviderAddressPairsApi } from "#ce/apps/SpFinderResultView/api/provider-address-pairs";
import { OtherLocationsStatus } from "#ce/components/SpFinder/view-models/other-locations-status";

const DECORATION_PARAMS = ["new_patient", "search_url"];

/**
 * Folds a provider result open to the provider's other addresses. A result's other addresses load on its first
 * expand and stay cached until the search changes.
 *
 * @param {Object} options
 * @param {import("vue").MaybeRefOrGetter<HTMLElement>} options.container the element the result cards sit in
 * @param {import("vue").MaybeRefOrGetter<Object>} options.settings the finder's results settings
 * @param {import("vue").MaybeRefOrGetter<Object>} options.payload the completed results payload
 * @param {import("vue").MaybeRefOrGetter<String>} options.searchUrl the completed search request URL
 * @param {Object} [options.api] a `createProviderAddressPairsApi` reader
 * @returns {{ otherLocations: Function, toggle: Function, retry: Function }}
 */
export function useOtherLocations({ container, settings, payload, searchUrl, api = createProviderAddressPairsApi() }) {
  const entries = shallowRef({});
  const controllers = new Map();
  const resultConfig = computed(() => toValue(settings)?.result);
  // The rows tell the addresses apart only by the address they show.
  const enabled = computed(
    () => resultConfig.value?.foldOutOtherLocations === true && Boolean(resultConfig.value?.showProviderAddress),
  );
  const gridWidth = computed(() => toValue(payload)?.appointment_grid?.dates?.length);

  function entry(result) {
    return entries.value[result.resultGUID];
  }

  function write(result, changes) {
    entries.value = { ...entries.value, [result.resultGUID]: { ...entry(result), ...changes } };
  }

  /** Returns the fold-out state a provider card draws, or undefined when the result has no fold-out. */
  function otherLocations(result) {
    const count = otherAddressPairs(result).length;

    if (!enabled.value || count === 0) {
      return undefined;
    }

    const current = entry(result) ?? { expanded: false, status: OtherLocationsStatus.IDLE, results: [] };

    return {
      count,
      expanded: current.expanded,
      status: current.status,
      results: current.results.map((other) => prepare(other, result, gridWidth.value)),
    };
  }

  async function load(result) {
    const controller = new AbortController();
    const pairs = otherAddressPairs(result);
    controllers.set(result.resultGUID, controller);
    write(result, { status: OtherLocationsStatus.LOADING });

    try {
      const results = await api.results(toValue(searchUrl), pairs, { signal: controller.signal });

      if (!controller.signal.aborted) {
        write(result, { status: OtherLocationsStatus.READY, results });
      }
    } catch {
      if (!controller.signal.aborted) {
        write(result, { status: OtherLocationsStatus.ERROR, results: [] });
      }
    } finally {
      if (controllers.get(result.resultGUID) === controller) {
        controllers.delete(result.resultGUID);
      }
    }
  }

  /** Expands a collapsed result, loading its other addresses the first time, and collapses an expanded one. */
  function toggle(result) {
    if (!otherLocations(result)) {
      return;
    }

    const current = entry(result);
    const expanded = !current?.expanded;
    write(result, { status: current?.status ?? OtherLocationsStatus.IDLE, results: current?.results ?? [], expanded });

    if (expanded && !current) {
      load(result);
    }
  }

  /** Loads a result's other addresses again after a failed load. */
  function retry(result) {
    if (entry(result)?.status === OtherLocationsStatus.ERROR) {
      load(result);
    }
  }

  function reset() {
    for (const controller of controllers.values()) {
      controller.abort();
    }

    controllers.clear();
    entries.value = {};
  }

  function resultFor(event) {
    const guid = event.detail?.[0]?.resultGuid;

    return toValue(payload)?.results?.find((result) => result.resultGUID === guid);
  }

  function handleToggle(event) {
    const result = resultFor(event);

    if (result) {
      toggle(result);
    }
  }

  function handleRetry(event) {
    const result = resultFor(event);

    if (result) {
      retry(result);
    }
  }

  const options = { capture: true };
  useEventListener(container, "toggle-other-locations", handleToggle, options);
  useEventListener(container, "retry-other-locations", handleRetry, options);
  watch([() => toValue(searchUrl), enabled], reset);
  onScopeDispose(reset);

  return { otherLocations, toggle, retry };
}

function otherAddressPairs(result) {
  if (result.resultType !== "Physician" || !Array.isArray(result.other_address_ids)) {
    return [];
  }

  return result.other_address_ids.map((addressId) => ({ physician_id: result.id, address_id: addressId }));
}

/**
 * Returns an other-address result shaped like the parent card's result: its own `resultGUID`, the parent's link
 * decoration, no past slots, and no more slot days than the list's appointment grid shows.
 */
function prepare(other, parent, gridWidth) {
  const prepared = { ...other, resultGUID: `${parent.resultGUID}:${other.address_id}` };
  prepared.url = decoratedUrl(other.url, parent.url);
  const slotDays = upcomingSlotDays(other.appointment_slots, gridWidth);

  if (slotDays) {
    prepared.appointment_slots = slotDays;
  } else {
    delete prepared.appointment_slots;
  }

  return prepared;
}

function decoratedUrl(url, parentUrl) {
  if (!url || !parentUrl) {
    return url;
  }

  const decorated = new URL(url, window.location.href);
  const parentParams = new URL(parentUrl, window.location.href).searchParams;

  for (const name of DECORATION_PARAMS) {
    if (parentParams.has(name)) {
      decorated.searchParams.set(name, parentParams.get(name));
    }
  }

  return decorated.href;
}

function upcomingSlotDays(slotDays, gridWidth) {
  if (!Array.isArray(slotDays)) {
    return undefined;
  }

  const now = new Date();
  const days = slotDays
    .slice(0, gridWidth ?? slotDays.length)
    .map((day) => ({ ...day, slots: (day.slots ?? []).filter((slot) => new Date(slot.start_time) > now) }));

  return days.some((day) => day.slots.length > 0) ? days : undefined;
}
